import type { BatteryArbitragePresentationDto } from "~/api/dto/battery-arbitrage.dto"

/**
 * The saved result if the presentation can state it honestly, or null.
 *
 * The server's contract requires finite money, so this is the client's answer
 * to a server that broke its own contract: showing "$\u221e" or "$NaN" beside a
 * bill claim is worse than showing no savings at all. It runs once where the
 * payload enters the presentation, so every section makes the same call and the
 * page cannot end up half-populated.
 *
 * It must answer, never throw. The anonymous presentation and the brochure run
 * it above their own error boundary, so a throw here blanks the homeowner's
 * page instead of showing the unavailable copy, and leaves the export waiting
 * out the renderer's whole budget. A payload whose shape is wrong is simply a
 * payload with no result in it.
 */
export const renderableArbitrage = (
  arbitrage: BatteryArbitragePresentationDto | null | undefined,
): BatteryArbitragePresentationDto | null => {
  if (
    !arbitrage ||
    (arbitrage.estimateBasis !== `personalized` &&
      arbitrage.estimateBasis !== `generalized`) ||
    (arbitrage.modeledBillingScope !== `complete_bill` &&
      arbitrage.modeledBillingScope !== `grid_purchases_only`) ||
    !arbitrage.assumptions ||
    typeof arbitrage.assumptions.loadBasis !== `string` ||
    typeof arbitrage.assumptions.rateBasis !== `string` ||
    !Array.isArray(arbitrage.assumptions.limitations) ||
    !Array.isArray(arbitrage.projections) ||
    // The chart reads these as arrays and the peak window is derived from the
    // rate curve, so a malformed series would break the whole document over an
    // optional savings block. The server publishes an unusable series as `[]`.
    !Array.isArray(arbitrage.representativeRateCurve) ||
    !Array.isArray(arbitrage.representativeDispatch)
  ) {
    return null
  }
  const validTotals = (before: number | null, after: number | null): boolean =>
    (before === null && after === null) ||
    (Number.isFinite(before) && Number.isFinite(after))
  const money = [
    arbitrage.annualSavingsUsd,
    ...arbitrage.projections.flatMap((year) => [
      year?.savingsUsd,
      year?.cumulativeUsd,
    ]),
  ]
  return money.every((value) => Number.isFinite(value)) &&
    validTotals(arbitrage.beforeAnnualBillUsd, arbitrage.afterAnnualBillUsd) &&
    arbitrage.projections.every((year) =>
      validTotals(year?.doNothingUsd, year?.withBatteryUsd),
    )
    ? arbitrage
    : null
}

/**
 * Whether the saved result reduces the bill by an amount the page actually
 * shows. Every figure on these surfaces is rendered to whole dollars, so a
 * saved saving of $0.40 prints as `$0` beside two identical bill columns \u2014
 * calling that "taking control" claims a benefit the figures do not show.
 * The threshold therefore follows the rounding, not the stored float.
 */
export const reducesUtilityBill = (annualSavingsUsd: number): boolean =>
  Math.round(annualSavingsUsd) > 0

const usdWhole = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
  maximumFractionDigits: 0,
})

/** Whole-dollar USD. Negatives carry a true minus sign, like `formatUsdExact`,
 * so two money figures on one page cannot disagree about the glyph. */
export const formatUsd = (value: number): string =>
  value < 0 ? `\u2212${usdWhole.format(-value)}` : usdWhole.format(value)

const usdExact = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Two-decimal USD; negatives keep a true minus sign rather than being clamped. */
export const formatUsdExact = (value: number): string =>
  value < 0 ? `\u2212${usdExact.format(-value)}` : usdExact.format(value)

const usdPerKwh = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
  minimumFractionDigits: 3,
  maximumFractionDigits: 5,
})

/** Modeled import rates. Three to five decimals so $0.37 and $0.374 stay distinct. */
export const formatUsdPerKwh = (value: number): string => {
  const amount =
    value < 0 ? `\u2212${usdPerKwh.format(-value)}` : usdPerKwh.format(value)
  return `${amount}/kWh`
}

export const formatCreatedDate = (createdAt: string): string | null => {
  const created = new Date(createdAt)
  if (Number.isNaN(created.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat(`en-US`, {
    month: `long`,
    day: `numeric`,
    year: `numeric`,
    timeZone: `UTC`,
  }).format(created)
}
